/* Milliard theme — predictive search.
   <predictive-search> lives in the header search overlay. Fetches Shopify's
   Predictive Search API rendered through sections/predictive-search.liquid. */
(() => {
  'use strict';

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('.search-overlay__results');
      this.form = this.querySelector('form');
      if (!this.input || !this.results) return;
      if (this.dataset.enabled === 'false' || !window.MBTheme?.settings?.predictiveSearch) return;

      this.abortController = null;
      this.input.addEventListener(
        'input',
        window.MBTheme.debounce(() => this.onChange(), 250)
      );
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));
      this.addEventListener('focusout', () => {
        requestAnimationFrame(() => {
          if (!this.contains(document.activeElement)) this.close();
        });
      });
      /* Safari + Firefox/macOS don't focus links on mousedown: the input's
         focusout would close() and empty the results mid-click, swallowing
         the navigation. Keeping focus in the input sidesteps it. */
      this.results.addEventListener('pointerdown', (e) => e.preventDefault());
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const query = this.getQuery();
      if (query.length < 2) {
        this.abortController?.abort(); // a late response must not reopen over a cleared input
        this.close();
        return;
      }
      this.fetchResults(query);
    }

    async fetchResults(query) {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.setAttribute('loading', 'true');
      const url = `${window.MBTheme.routes.predictiveSearch}?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article,query&resources[limit]=8&resources[limit_scope]=each&section_id=predictive-search`;
      try {
        const response = await fetch(url, { signal: this.abortController.signal });
        if (!response.ok) throw new Error(String(response.status));
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const inner = doc.querySelector('#shopify-section-predictive-search') || doc.body;
        this.results.innerHTML = inner.innerHTML;
        this.selectedIndex = -1; // stale highlight must not survive re-render
        this.input.removeAttribute('aria-activedescendant');
        this.open();
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.close();
      } finally {
        this.removeAttribute('loading');
      }
    }

    open() {
      this.setAttribute('results', 'true');
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.removeAttribute('results');
      this.results.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
      this.selectedIndex = -1;
    }

    options() {
      const opts = Array.from(this.results.querySelectorAll('a[data-predictive-option]'));
      opts.forEach((opt, i) => {
        if (!opt.id) opt.id = `predictive-option-${i}`;
      });
      return opts;
    }

    onKeydown(e) {
      const options = this.options();
      if (!options.length) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const delta = e.key === 'ArrowDown' ? 1 : -1;
        this.selectedIndex = ((this.selectedIndex ?? -1) + delta + options.length) % options.length;
        options.forEach((opt, i) => {
          const isOption = opt.getAttribute('role') === 'option';
          opt.classList.toggle('is-active', i === this.selectedIndex);
          if (i === this.selectedIndex) {
            /* aria-selected/activedescendant only apply to real listbox
               options — suggestion/page/view-all links get the visual
               highlight but must not corrupt the combobox pattern */
            if (isOption) {
              opt.setAttribute('aria-selected', 'true');
              this.input.setAttribute('aria-activedescendant', opt.id || '');
            } else {
              this.input.removeAttribute('aria-activedescendant');
            }
            opt.scrollIntoView({ block: 'nearest' });
          } else if (isOption) {
            opt.removeAttribute('aria-selected');
          }
        });
      } else if (e.key === 'Enter' && this.selectedIndex >= 0 && options[this.selectedIndex]) {
        e.preventDefault();
        window.location.href = options[this.selectedIndex].href;
      }
    }
  }

  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch);
  }
})();
